/**
 * M27 Steps 6 / 7 / 8 — Approval Chains, Integrations, Shadow Payroll.
 */

import {
  AlertTriangle,
  Banknote,
  Building2,
  Calculator,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileSpreadsheet,
  Globe,
  MessageSquare,
  Plug,
  ShieldCheck,
  Sparkles,
  UserMinus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AIHelpsHereCallout } from "../../components/onboarding/AIHelpsHereCallout";
import { OnboardingIntegrationCard } from "../../components/onboarding/OnboardingIntegrationCard";
import { StepLayout } from "./StepLayout";

// ===========================================================================
// Step 6 — Approval Chains
// ===========================================================================

const _WORKFLOW_KEYS = [
  {
    key: "leave_request",
    labelKey: "app.s6_wf_leave_label",
    icon: CalendarDays,
    bodyKey: "app.s6_wf_leave_body",
  },
  {
    key: "loan_application",
    labelKey: "app.s6_wf_loan_label",
    icon: Banknote,
    bodyKey: "app.s6_wf_loan_body",
  },
  {
    key: "excuse_request",
    labelKey: "app.s6_wf_excuse_label",
    icon: ClipboardCheck,
    bodyKey: "app.s6_wf_excuse_body",
  },
  {
    key: "termination",
    labelKey: "app.s6_wf_termination_label",
    icon: UserMinus,
    bodyKey: "app.s6_wf_termination_body",
  },
];

export function Step6ApprovalsReal() {
  const { t } = useTranslation();
  return (
    <StepLayout
      slug="approvals"
      title={t("app.onb_step_approvals")}
      subtitle={t("app.s6_subtitle")}
      continueLabel={t("app.s6_continue_to_integrations")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s6_ai_msg")}
          detail={t("app.s6_ai_detail")}
        />

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {_WORKFLOW_KEYS.map(({ key, labelKey, icon: Icon, bodyKey }) => (
            <article
              key={key}
              className="rounded-xl border border-border-subtle bg-surface-default p-4"
            >
              <header className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ai-bg text-ai">
                  <Icon size={14} aria-hidden />
                </span>
                <h2 className="text-sm font-semibold text-text-primary">
                  {t(labelKey)}
                </h2>
              </header>
              <p className="mt-2 text-xs text-text-secondary">{t(bodyKey)}</p>
              <Link
                to={`/policies/approval-chains/${key}`}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
              >
                {t("app.s6_edit_flow")}
                <ChevronRight size={14} aria-hidden className="rtl:rotate-180" />
              </Link>
            </article>
          ))}
        </section>

        <p className="text-xs text-text-tertiary">
          {t("app.s6_footer_1")}{" "}
          <Link
            to="/policies/approval-chains"
            className="text-brand hover:underline"
          >
            {t("app.s6_footer_link")}
          </Link>{" "}
          {t("app.s6_footer_2")}
        </p>
      </div>
    </StepLayout>
  );
}

// ===========================================================================
// Step 7 — Integrations
// ===========================================================================

const _GOV_KEYS = [
  {
    key: "mudad",
    labelKey: "app.s7_gov_mudad_label",
    bodyKey: "app.s7_gov_mudad_body",
  },
  {
    key: "gosi",
    labelKey: "app.s7_gov_gosi_label",
    bodyKey: "app.s7_gov_gosi_body",
  },
  {
    key: "muqeem",
    labelKey: "app.s7_gov_muqeem_label",
    bodyKey: "app.s7_gov_muqeem_body",
  },
  {
    key: "qiwa",
    labelKey: "app.s7_gov_qiwa_label",
    bodyKey: "app.s7_gov_qiwa_body",
  },
];

export function Step7IntegrationsReal() {
  const { t } = useTranslation();
  return (
    <StepLayout
      slug="integrations"
      title={t("app.onb_step_integrations")}
      subtitle={t("app.s7_subtitle")}
      continueLabel={t("app.s7_continue_to_shadow")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s7_ai_msg")}
          detail={t("app.s7_ai_detail")}
        />

        <OnboardingIntegrationCard
          integrationType="twilio"
          titleKey="app.s7_twilio_title"
          bodyKey="app.s7_twilio_body"
          icon={MessageSquare}
          needsLegalEntityHintKey="app.onb_integration_needs_le"
        />

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="flex items-center gap-2">
            <Globe size={16} className="text-brand-blue" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s7_gov_title")}
            </h2>
            <span className="ms-auto rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-text-tertiary">
              {t("app.s7_gov_status")}
            </span>
          </header>
          <p className="mt-1 text-xs text-text-secondary">
            {t("app.s7_gov_body")}
          </p>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {_GOV_KEYS.map((g) => (
              <li
                key={g.key}
                className="flex items-start gap-2 rounded border border-border-subtle bg-surface-sunken px-3 py-2"
              >
                <ShieldCheck
                  size={12}
                  aria-hidden
                  className="mt-0.5 text-text-tertiary"
                />
                <span>
                  <span className="block text-xs font-medium text-text-primary">
                    {t(g.labelKey)}
                  </span>
                  <span className="block text-xs text-text-tertiary">
                    {t(g.bodyKey)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
          <header className="flex items-center gap-2">
            <Plug size={14} className="text-text-secondary" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s7_secondary_title")}
            </h2>
          </header>
          <ul className="mt-2 flex flex-col gap-1.5 text-xs text-text-secondary">
            <li>
              <strong>{t("app.s7_secondary_anthropic")}</strong>{" "}
              {t("app.s7_secondary_anthropic_body")}
            </li>
            <li>
              <strong>{t("app.s7_secondary_teams")}</strong>{" "}
              {t("app.s7_secondary_teams_body")}
            </li>
          </ul>
        </section>
      </div>
    </StepLayout>
  );
}

// ===========================================================================
// Step 8 — Shadow Payroll
// ===========================================================================

export function Step8ShadowPayrollReal() {
  const { t } = useTranslation();
  return (
    <StepLayout
      slug="shadow-payroll"
      title={t("app.onb_step_shadow_payroll")}
      subtitle={t("app.s8_subtitle")}
      continueLabel={t("app.s8_continue_to_golive")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s8_ai_msg")}
          detail={t("app.s8_ai_detail")}
        />

        <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-5">
          <header className="flex items-start gap-2">
            <AlertTriangle
              size={16}
              className="mt-0.5 text-amber-700"
              aria-hidden
            />
            <div>
              <h2 className="text-sm font-semibold text-amber-900">
                {t("app.s8_suggested_title")}
              </h2>
              <p className="mt-1 text-xs text-amber-800">
                {t("app.s8_suggested_body_1")}{" "}
                <Link
                  to="/operations/payroll"
                  className="text-brand hover:underline"
                >
                  {t("app.s8_suggested_body_link")}
                </Link>
                {t("app.s8_suggested_body_2")}
              </p>
            </div>
          </header>
        </section>

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="flex items-center gap-2">
            <Calculator size={16} className="text-brand-blue" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s8_run_comparison_title")}
            </h2>
            <Link
              to="/operations/payroll"
              className="ms-auto inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
            >
              {t("app.s8_open_payroll")}
              <ChevronRight size={14} aria-hidden className="rtl:rotate-180" />
            </Link>
          </header>
          <p className="mt-1 text-xs text-text-secondary">
            {t("app.s8_run_comparison_body")}
          </p>
        </section>

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="mb-2 flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-brand-blue" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s8_v2_title")}
            </h2>
          </header>
          <ul className="flex flex-col gap-1.5 text-xs text-text-secondary">
            <li>
              <strong>{t("app.s8_v2_default_tolerance")}</strong>{" "}
              {t("app.s8_v2_default_tolerance_body")}
            </li>
            <li>
              <strong>{t("app.s8_v2_per_delta")}</strong>{" "}
              {t("app.s8_v2_per_delta_body")}
            </li>
            <li>
              <strong>{t("app.s8_v2_lock")}</strong>{" "}
              {t("app.s8_v2_lock_body")}
            </li>
          </ul>
        </section>

        <p className="text-xs text-text-tertiary">
          {t("app.s8_footer_skip")}
        </p>
      </div>
    </StepLayout>
  );
}

export { Sparkles as SolvaitMark, Building2 as LeMark, CheckCircle2 };
